import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams } from "react-router-dom";
import { auth } from './core/internal/FirebaseConfig';
import { authService } from './core/AuthService';
import { firebaseRepository } from './core/FirebaseRepository';



export const useAppViewModel = () => {
  const navigate = useNavigate();
  const { webIdParam, pageIdParam } = useParams();

  // --- State ---
  const [currentUser, setCurrentUser] = useState(auth.currentUser);
  const [webIds, setWebIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState({ webId: webIdParam?.split("#")[0], pageId: pageIdParam?.split("#")[0] });
  const [web, setWeb] = useState({});
  const [currentPageData, setCurrentPageData] = useState("");

  const [expanded, setExpanded] = useState(true);
  const [users, setUsers] = useState([]);
  const [openPagesMap, setOpenPagesMap] = useState({});
  const [showingAddPage, setShowingAddPage] = useState(false);
  const [newPageName, setNewPageName] = useState("");
  const [newPageParent, setNewPageParent] = useState(undefined);
  const [isMoving, setIsMoving] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const initializedRef = useRef(false)

  // --- Helper: Manifest ---
  const setManifest = useCallback(() => {
    const manifest = {
      start_url: window.location.pathname,
      short_name: "Webbed Notes",
      name: "Webbed Notes",
      display: "standalone",
    };
    const blob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('link');
    link.rel = 'manifest';
    link.href = url;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
      URL.revokeObjectURL(url);
    };
  }, []);

  const updateNavigate = useCallback((wId, pId, name) => {
    console.log("updateNavigate", wId, pId, name)
    if (wId == null) {
      navigate("/");
    } else if (pId == null) {
      navigate(`/${wId}`);
    } else {
      const anchor = name?.length > 0 ? `#${name.replaceAll(" ", "_")}` : "";
      navigate(`/${wId}/${pId}${anchor}`);
    }
    setManifest();
  }, [navigate, setManifest]);

  const updateNavigateRef = useRef(updateNavigate)

  useEffect(() => {
    updateNavigateRef.current = updateNavigate
  }, [updateNavigate])

  // --- Navigation & Selection ---
  const setSelection = (wId, pId) => {
    console.log("setSelection", wId, pId)
    setSelectedIds({ webId: wId, pageId: pId })
    updateNavigateRef.current(wId, pId)
  }

  // --- Data Fetching ---
  const getPage = useCallback(async (webId, pageId) => {
    console.trace("getPage", webId, pageId)
    setSelection(webId, pageId)

  }, []);

  const handleWebIds = useCallback(spaces => {
    console.log("handleWebIds", spaces, spaces?.[0])
    setWebIds(spaces)
  }, [])

  //If no web is selected but there are some available, select the first one
  useEffect(() => {
    if (selectedIds.webId == null && webIds.length > 0) {
      setSelection(webIds[0], null)
    }
  }, [webIds, selectedIds])

  const handleWebIdsRef = useRef(handleWebIds)
  useEffect(() => {
    handleWebIdsRef.current = handleWebIds
  }, [handleWebIds])

  //Fetch Web and user data when the web changes
  useEffect(() => {
    (async () => {
      console.log("fetchWeb", selectedIds.webId)
      if (selectedIds.webId != null) {
        let webData = await firebaseRepository.getWeb(selectedIds.webId);
        setWeb(webData ? webData : {});

        setUsers(await firebaseRepository.getUsers(selectedIds.webId));
      }
    })()
  }, [selectedIds.webId]);

  //Fetch page data when page changes
  useEffect(() => {
    (async () => {
      console.log("fetchPage", selectedIds.webId, selectedIds.pageId)
      if (selectedIds.webId != null && selectedIds.pageId != null) {

        firebaseRepository.subscribeToPage(selectedIds.webId, selectedIds.pageId, data => {
          setCurrentPageData(data)
          updateNavigateRef.current(selectedIds.webId, selectedIds.pageId, data.name);
        })

      }
    })()
  }, [selectedIds]);

  // --- Auth Management ---
  const handleAuthChange = useCallback(user => {
    console.log("handleAuthChange", "user", user?.email)
    setCurrentUser(prevUser => {
      console.log("prevUser", prevUser, "user", user?.email)
      if (user && user.email !== prevUser?.email) {
        return user;
      } else if (!user) {
        return null;
      }
      return prevUser;
    });
  }, []);

  useEffect(() => {
    console.log("useEffect currentUser", currentUser, "initialized", initialized)
    if (!initializedRef.current) {
      initializedRef.current = true
      setInitialized(true)
      return
    }
    if (currentUser) {
      console.log("user change", currentUser.email)
      firebaseRepository.subscribeToWebs(handleWebIdsRef.current)
      setWeb({});
      setShowingAddPage(false);
      setCurrentPageData("");
    } else {
      setWeb({});
      setSelection(undefined, undefined);
      authService.signIn();
    }
  }, [currentUser, initialized, setInitialized])

  const authHandlerRef = useRef(handleAuthChange);
  useEffect(() => { authHandlerRef.current = handleAuthChange; }, [handleAuthChange]);

  useEffect(() => {
    const unsubscribe = authService.subscribeToAuth(user => authHandlerRef.current(user));
    return () => unsubscribe();
  }, []);

  // --- Lifecycle Listeners ---
  // useEffect(() => {
  //   if (currentUser) {
  //     fetchData(selectedIds.webId, selectedIds.pageId);
  //   }
  // }, [currentUser, selectedIds, fetchData, web.rootPages?.length]);

  // --- Page Operations (Add, Delete, Move, Save) ---
  const add = (parent) => {
    if (parent !== undefined) {
      setOpenPagesMap(m => ({ ...m, [parent]: true }));
    }
    setShowingAddPage(true);
    setNewPageParent(parent);
  };

  const addBlankPage = async (name, parent, callback) => {
    setShowingAddPage(false);
    let webIdToUse = selectedIds.webId || await firebaseRepository.createWeb(auth.currentUser.uid, auth.currentUser.email);
    let pageData = await firebaseRepository.addPage(webIdToUse, name);

    console.log("New Page", pageData)

    let treeCopy = { ...web, pages: { ...web.pages } };
    let page = { id: pageData.id, name, pages: [], parent };

    if (parent !== undefined) {
      treeCopy.pages[parent].pages.push(page.id);
    } else {
      treeCopy.rootPages.push(page.id);
    }
    treeCopy.pages[page.id] = page;

    await firebaseRepository.setWeb(webIdToUse, treeCopy);
    setWeb(treeCopy);
    setNewPageName("");
    callback(page.id);
  };

  const deletePage = async (id) => {
    let rootPages = [...web.rootPages];
    let pagesObj = { ...web.pages };
    let targetId = selectedIds.pageId; // Assuming logic targets selected page

    let parentId = pagesObj[targetId].parent;
    if (parentId === undefined) {
      pagesObj[targetId].pages.forEach(child => delete pagesObj[child].parent);
      rootPages = rootPages.flatMap(p => p === targetId ? pagesObj[targetId].pages : [p]);
    } else {
      pagesObj[targetId].pages.forEach(child => pagesObj[child].parent = parentId);
      pagesObj[parentId].pages = pagesObj[parentId].pages.flatMap(p => p === targetId ? pagesObj[targetId].pages : [p]);
    }

    delete pagesObj[targetId];
    let data = { rootPages: rootPages, pages: pagesObj }
    await firebaseRepository.setWeb(selectedIds.webId, data);
    setWeb(data);
    setSelection(selectedIds.webId, undefined);
    setCurrentPageData("");
  };

  const savePage = async (pageData, updatedReferences) => {
    firebaseRepository.setPage(selectedIds.webId, selectedIds.pageId, pageData);
    if (updatedReferences) {
      for (let ref of updatedReferences) {
        firebaseRepository.updatePage(selectedIds.webId, ref.id, {
          [`referencedFrom.${selectedIds.pageId}.${ref.block}`]: ref.text
        });
      }
    }
  };

  const saveChange = (data) => firebaseRepository.saveChange(users, selectedIds.webId, selectedIds.pageId, data);

  const movePage = async (newParent) => {
    setIsMoving(false);
    let webCopy = JSON.parse(JSON.stringify(web)); // Deep copy
    let oldParentId = webCopy.pages[selectedIds.pageId].parent;

    if (oldParentId === undefined) {
      webCopy.rootPages = webCopy.rootPages.filter(p => p !== selectedIds.pageId);
    } else {
      webCopy.pages[oldParentId].pages = webCopy.pages[oldParentId].pages.filter(p => p !== selectedIds.pageId);
    }

    if (newParent === undefined) {
      delete webCopy.pages[selectedIds.pageId].parent;
      webCopy.rootPages.push(selectedIds.pageId);
    } else {
      webCopy.pages[selectedIds.pageId].parent = newParent;
      webCopy.pages[newParent].pages.push(selectedIds.pageId);
    }

    setWeb(webCopy);
    await firebaseRepository.setWeb(selectedIds.webId, webCopy);
  };

  return {
    state: {
      currentUser, expanded, webIds: webIds, web: web, users, openPagesMap,
      showingAddPage, newPageName, currentPageData, pageId: selectedIds.pageId,
      webId: selectedIds.webId, isMoving
    },
    actions: {
      setExpanded, setSelection, add, addPage: () => addBlankPage(newPageName, newPageParent, () => { }),
      getPage,
      addBlankPage, deletePage, savePage, saveChange, movePage,
      setNewPageName, setOpenPagesMap, setIsMoving,
      signOut: () => {
        setSelection(null, null)
        authService.signOutUser()
      }
    }
  };
};